import React, { useEffect } from 'react';
import { View, Modal, ActivityIndicator } from 'react-native';
import { translate } from '../../../services/translate';
import FormInput from '../../../components/FormInput';
import FormActions from '../../../components/FormActions';
import { licensePlateRecognitionService, LPRServiceError, PlateData, PlateType } from '../../../services/central-api/license-plate-recognition';
import { WindowInformation } from '../../../services/window-information';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import FilterSwitch from '../../../components/Filter/components/FilterSwitch';
import Toast from 'react-native-toast-message';
import { useAuth } from '../../../context/Auth';
import { useForm, useWatch } from 'react-hook-form';
import PlateTypeDropdown from '../../../components/lpr/PlateTypeDropdown';
import { useQuery } from '@tanstack/react-query';
import moment from 'moment';
import { regPlate } from '../../../components/helpers/plate';

interface ModalProps {
    isModalVisible: boolean;
    onClose: () => void;
    plateId?: number;
}

export default function PlatesModal({ onClose, plateId, isModalVisible }: ModalProps) {
    const { styles, theme } = useStyles(styleSheet);
    const windowInfo = WindowInformation();
    const { user: authenticatedUser } = useAuth();

    const queryResult = useQuery({
        queryKey: ['plateModal', plateId],
        queryFn: () => licensePlateRecognitionService.getPlate(plateId!),
        enabled: !!plateId && !!authenticatedUser,
    });

    const isEnabled = !!plateId && !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isLoading;

    const { data, refetch } = queryResult;

    const defaultEmptyValues: Omit<PlateData, 'id'> = {
        plate: '',
        description: '',
        isActive: true,
        type: PlateType.DENY_LIST,
        validity: undefined
    };

    const form = useForm<Omit<PlateData, 'id'>>({
        defaultValues: defaultEmptyValues,
        mode: 'onChange'
    });

    const type = useWatch({ control: form.control, name: 'type' });
    const validity = useWatch({ control: form.control, name: 'validity' });

    async function formSubmit() {
        try {
            const data = form.getValues();

            if (plateId) {
                await licensePlateRecognitionService.updatePlate({
                    id: plateId,
                    description: data.description,
                    isActive: data.isActive
                });
            } else {
                await licensePlateRecognitionService.createPlate({
                    plate: data.plate,
                    description: data.description,
                    isActive: data.isActive,
                    type: data.type
                });
            }
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            onClose();
        } catch (err) {
            if (err instanceof LPRServiceError) {
                if (err.message === 'PlateAlreadyExists') {
                    return Toast.show({
                        type: 'sentinelxError',
                        text1: translate('plateAlreadyRegistered')
                    });
                }
            }
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    useEffect(() => {
        if (!authenticatedUser || !data) {
            return;
        }
        form.reset({
            ...data,
        });

    }, [plateId, data, authenticatedUser]);


    useEffect(() => {
        if (!isModalVisible || !authenticatedUser) {
            return;
        }

        if (plateId) {
            refetch();
        } else {
            form.reset(defaultEmptyValues);
        }
    }, [plateId, authenticatedUser, isModalVisible]);

    return (
        <Modal
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => {
                onClose();
            }}
            transparent={true}
        >
            <View style={styles.container}>
                <View style={[styles.formContainer, windowInfo.isMobile ? { width: '95%' } : { width: '30%' }]}>
                    {isLoading ?
                        <ActivityIndicator size='large' color={theme.colors.loadingColor} />
                        : <View style={{ rowGap: 25 }}>
                            <PlateTypeDropdown
                                zIndex={3}
                                form={form}
                                fieldName='type'
                                disabled={!!plateId}
                                includeDebris={!!plateId}
                            />
                            <FormInput
                                label={translate('plate')}
                                required={true}
                                form={form}
                                fieldName='plate'
                                disabled={!!plateId}
                                maxLength={7}
                                pattern={{
                                    value: regPlate,
                                    message: translate('invalidPlate')
                                }}
                            />
                            <FormInput
                                label={translate('description')}
                                required={true}
                                form={form}
                                fieldName='description'
                                multiline
                                numberOfLines={3}
                                disabled={type == PlateType.ILLEGAL_DEBRIS}
                            />
                            <FilterSwitch
                                label={translate('active')}
                                direction='row'
                                form={form}
                                fieldName='isActive'
                                disabled={type == PlateType.ILLEGAL_DEBRIS}
                            />
                            {
                                validity && <FormInput
                                    label={translate('validity')}
                                    disabled={true}
                                    value={moment(validity).format('DD/MM/YYYY')}
                                    onChangeText={() => undefined}
                                />
                            }
                        </View>}
                    <FormActions
                        onSubmit={formSubmit}
                        onClose={onClose}
                        disabled={!form.formState.isValid || type == PlateType.ILLEGAL_DEBRIS} />
                </View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1
    },
    formContainer: {
        minWidth: 300,
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        padding: 20,
        rowGap: 25
    }
}));
